import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Collapse, Link, makeStyles, Typography } from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import ArrowDropDownIcon from "@material-ui/icons/ArrowDropDown";
import ErrorIcon from "@material-ui/icons/Error";
import { getTestErrorKey, Outcome } from "../helpers";
import { useResultStyles } from "./styles";

interface Props {
  outcome: Outcome;
  providerName: string;
  onClose: () => void;
}

export default function Failure({ outcome, providerName, onClose }: Props) {
  const { t } = useTranslation("storage");
  const resultClasses = useResultStyles();
  const classes = useStyles();
  const [showDetails, setShowDetails] = useState(
    outcome.code === "AI_PROVIDER_REJECTED"
  );
  const { probe = {} } = outcome;
  const details = getDetails(probe);
  const hasStatus = Boolean(probe.status);
  const titleKey =
    outcome.mode === "save"
      ? "externalAI.result.saveFailed"
      : "externalAI.result.testFailed";
  const toggleKey = showDetails
    ? "externalAI.result.hideDetails"
    : "externalAI.result.viewDetails";
  const captionKey = probe.error
    ? "externalAI.result.transportCaption"
    : "externalAI.result.detailsCaption";
  const toggleIconClass = showDetails
    ? `${classes.toggleIcon} ${classes.toggleIconOpen}`
    : classes.toggleIcon;
  const alertClasses = {
    message: classes.message,
    action: classes.failureAction,
  };

  return (
    <Alert
      severity="error"
      icon={<ErrorIcon />}
      onClose={onClose}
      className={`${resultClasses.result} ${classes.failure}`}
      classes={alertClasses}
      data-testid="external-ai__result-error"
    >
      <AlertTitle className={classes.title}>
        {t(titleKey)}
        {hasStatus && (
          <span className={classes.statusChip}>HTTP {probe.status}</span>
        )}
      </AlertTitle>
      {t(getTestErrorKey(outcome.code))}
      {details && (
        <>
          <div>
            <Link
              component="button"
              type="button"
              variant="body2"
              underline="none"
              className={classes.detailsToggle}
              aria-expanded={showDetails}
              onClick={() => setShowDetails(prev => !prev)}
            >
              {t(toggleKey)}
              <ArrowDropDownIcon className={toggleIconClass} />
            </Link>
          </div>
          <Collapse in={showDetails}>
            <Typography
              variant="caption"
              color="textSecondary"
              className={classes.caption}
            >
              {t(captionKey, { provider: providerName })}
            </Typography>
            <pre className={classes.details}>{details}</pre>
          </Collapse>
        </>
      )}
    </Alert>
  );
}

function getDetails(probe: Outcome["probe"] = {}) {
  if (probe.error) return probe.error;

  const lines = [];
  if (probe.status) lines.push(`HTTP ${probe.status}`);
  if (probe.body) lines.push(probe.body);

  return lines.join("\n");
}

const useStyles = makeStyles(theme => ({
  failure: { borderColor: "#f3c4c4", backgroundColor: "#fff4f4" },
  message: { width: "100%" },
  failureAction: { alignItems: "flex-start", color: theme.palette.error.main },
  title: { display: "flex", alignItems: "center", gap: 8, fontWeight: 500 },
  statusChip: {
    padding: "1px 7px",
    border: "1px solid #f3c4c4",
    borderRadius: 4,
    backgroundColor: "#fff",
    color: theme.palette.error.main,
    fontFamily: "monospace",
    fontSize: 12,
    fontWeight: 400,
  },
  detailsToggle: {
    display: "inline-flex",
    alignItems: "center",
    marginTop: 12,
    fontWeight: 500,
  },
  toggleIcon: { transition: "transform .2s" },
  toggleIconOpen: { transform: "rotate(180deg)" },
  caption: { display: "block", marginTop: 10 },
  details: {
    margin: "6px 0 0",
    padding: "12px 14px",
    borderRadius: 6,
    backgroundColor: "#2b2f36",
    color: "#e8eaed",
    fontSize: 12,
    lineHeight: 1.55,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
}));
